<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="560px">
    <v-card>
      <v-card-title class="headline">
        {{ $t("employee.createNewRole") }}
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="newRole.name"
          :label="$t('employee.roleName')"
          dense
          outlined
          hide-details="auto"
          :error-messages="nameError"
          @keyup.enter="createRole"
        />

        <div class="caption mt-3 mb-1">{{ $t("employee.roleIcon") }}</div>
        <div class="d-flex flex-wrap">
          <v-btn
            v-for="icon in iconChoices"
            :key="icon"
            icon
            small
            :color="newRole.icon === icon ? 'primary' : ''"
            class="ma-1 icon-choice"
            :class="{ selected: newRole.icon === icon }"
            @click="newRole.icon = icon"
          >
            <v-icon>{{ icon }}</v-icon>
          </v-btn>
        </div>

        <div class="d-flex align-center mt-3">
          <span class="caption mr-2">{{ $t("employee.preview") }}:</span>
          <v-chip small outlined>
            <v-icon left small>{{ newRole.icon }}</v-icon>
            {{ newRole.name || $t("employee.roleName") }}
          </v-chip>
        </div>

        <template v-if="existingRoles.length">
          <v-divider class="my-4" />
          <div class="caption mb-1">{{ $t("employee.existingRoles") }}</div>
          <div class="d-flex flex-wrap">
            <v-chip
              v-for="role in existingRoles"
              :key="role._id"
              small
              outlined
              class="ma-1"
              :close="canDeleteHere"
              close-icon="mdi-close"
              @click:close="askDelete(role)"
            >
              <v-icon left small>{{ role.icon || "mdi-account-star" }}</v-icon>
              {{ role.name }}
            </v-chip>
          </div>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-btn color="grey" outlined @click="close">
          {{ $t("general.cancel") }}
        </v-btn>
        <div class="flex-grow-1"></div>
        <v-btn color="secondary" text :loading="creating" @click="createRole">
          <v-icon left>mdi-plus</v-icon>
          {{ $t("employee.createNewRole") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="deleteDialog.open" max-width="440">
      <v-card>
        <v-card-title>{{ $t("employee.deleteRole") }}</v-card-title>
        <v-card-text>
          {{ $t("employee.deleteRoleConfirm", { name: deleteDialog.role ? deleteDialog.role.name : "" }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="deleteDialog.loading" @click="deleteDialog.open = false">
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn color="error" text :loading="deleteDialog.loading" @click="deleteRole">
            {{ $t("general.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
const ICON_CHOICES = [
  "mdi-account-star",
  "mdi-account-hard-hat",
  "mdi-heart-pulse",
  "mdi-medical-bag",
  "mdi-bandage",
  "mdi-school",
  "mdi-briefcase",
  "mdi-bed",
  "mdi-flag",
  "mdi-wrench",
  "mdi-truck",
  "mdi-leaf",
  "mdi-star",
  "mdi-clipboard-account",
  "mdi-account-group",
  "mdi-water",
  "mdi-lightning-bolt",
  "mdi-radioactive",
  "mdi-biohazard",
  "mdi-hard-hat",
  "mdi-safety-goggles",
  "mdi-ear-hearing",
  "mdi-hand-wash",
  "mdi-forklift",
  "mdi-tools",
  "mdi-factory",
  "mdi-warehouse",
  "mdi-office-building",
  "mdi-laptop",
  "mdi-phone",
  "mdi-email",
  "mdi-car",
  "mdi-airplane",
  "mdi-key",
  "mdi-lock",
  "mdi-clipboard-check",
  "mdi-calendar-clock",
  "mdi-chart-line",
  "mdi-cash",
  "mdi-account-tie",
  "mdi-fire-extinguisher",
  "mdi-stethoscope",
  "mdi-needle",
  "mdi-pill",
  "mdi-broom",
  "mdi-recycle",
  "mdi-shield-check",
  "mdi-clipboard-list",
  "mdi-ambulance",
  "mdi-thermometer",
  "mdi-magnify",
  "mdi-cog",
  "mdi-flask",
  "mdi-tshirt-crew",
  "mdi-snowflake",
  "mdi-hand-heart",
];

export default {
  name: "createRoleDialog",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      iconChoices: ICON_CHOICES,
      creating: false,
      nameError: "",
      deleteDialog: { open: false, role: null, loading: false },
      newRole: {
        name: "",
        icon: ICON_CHOICES[0],
      },
    };
  },
  computed: {
    existingRoles() {
      return this.$store.getters.employeeRoleDefinitions || [];
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetNewRole();
        this.$store.dispatch("getEmployeeRoleDefinitions");
      }
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    resetNewRole() {
      this.newRole = {
        name: "",
        icon: ICON_CHOICES[0],
      };
      this.nameError = "";
    },
    askDelete(role) {
      this.deleteDialog = { open: true, role, loading: false };
    },
    async deleteRole() {
      const role = this.deleteDialog.role;
      if (!role) return;
      this.deleteDialog.loading = true;
      try {
        await this.$store.dispatch("removeEmployeeRoleDefinition", role._id);
        this.deleteDialog.open = false;
        this.$emit("created");
      } finally {
        this.deleteDialog.loading = false;
      }
    },
    async createRole() {
      const name = (this.newRole.name || "").trim();
      if (!name) {
        this.nameError = this.$t("general.fieldRequired");
        return;
      }
      this.nameError = "";
      this.creating = true;
      try {
        await this.$store.dispatch("createEmployeeRoleDefinition", {
          name,
          icon: this.newRole.icon,
        });
        this.resetNewRole();
        this.$emit("created");
        this.close();
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<style scoped>
.icon-choice.selected {
  border: 1px solid var(--v-primary-base);
  border-radius: 6px;
}
</style>
